<template>
  <component
    :is="tag"
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    :style="buttonStyles"
    @click="handleClick"
  >
    <!-- Loading Spinner -->
    <span v-if="loading">
      <svg
        class="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </span>

    <!-- Icon (left) -->
    <component
      v-if="icon && iconPosition === 'left'"
      :is="icon"
      :size="iconSize"
    />

    <!-- Button Content -->
    <span v-if="$slots.default">
      <slot />
    </span>
    <span v-else>{{ label }}</span>

    <!-- Icon (right) -->
    <component
      v-if="icon && iconPosition === 'right'"
      :is="icon"
      :size="iconSize"
      class="ml-2"
    />

    <!-- Badge/Suffix -->
    <span v-if="badge" :class="badgeClasses">
      {{ badge }}
    </span>
  </component>
</template>

<script>
export default {
  name: "Button",
  props: {
    // Content
    label: {
      type: String,
      default: ""
    },
    badge: {
      type: [String, Number],
      default: null
    },
    icon: {
      type: [Object, Function],
      default: null
    },
    iconPosition: {
      type: String,
      default: "left",
      validator: (value) => ["left", "right"].includes(value)
    },
    iconSize: {
      type: Number,
      default: 16
    },
    // Variants
    variant: {
      type: String,
      default: "primary",
      validator: (value) =>
        [
          "primary",
          "secondary",
          "danger",
          "success",
          "warning",
          "ghost",
          "outline",
          "link"
        ].includes(value)
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value)
    },
    // States
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    // Layout
    fullWidth: {
      type: Boolean,
      default: false
    },
    circular: {
      type: Boolean,
      default: false
    },
    // HTML attributes
    type: {
      type: String,
      default: "button",
      validator: (value) => ["button", "submit", "reset"].includes(value)
    },
    tag: {
      type: String,
      default: "button",
      validator: (value) => ["button", "a", "router-link"].includes(value)
    },
    // Custom classes
    customClass: {
      type: String,
      default: ""
    }
  },
  computed: {
    buttonClasses() {
      return [
        "flex inline-flex items-center justify-center font-medium transition-all",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        this.sizeClasses,
        this.variantClasses,
        this.fullWidth ? "w-full" : "",
        this.circular ? this.circularClasses : "",
        this.disabled || this.loading
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer",
        this.customClass
      ]
        .filter(Boolean)
        .join(" ");
    },
    sizeClasses() {
      if (this.circular) {
        // For circular buttons, use uniform padding (same on all sides)
        const sizes = {
          sm: "text-sm p-2",
          md: "text-base p-3",
          lg: "text-lg p-4"
        };
        return sizes[this.size] || sizes.md;
      }
      const sizes = {
        sm: "text-sm px-3 py-1.5 rounded-lg",
        md: "text-base px-4 py-2.5 rounded-xl",
        lg: "text-lg px-6 py-3.5 rounded-xl"
      };
      return sizes[this.size] || sizes.md;
    },
    circularClasses() {
      return "rounded-full aspect-square";
    },
    variantClasses() {
      const variants = {
        primary:
          "bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 shadow-lg",
        secondary:
          "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 shadow-lg",
        danger:
          "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-lg",
        success:
          "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-lg",
        warning:
          "bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 shadow-lg",
        ghost:
          "bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
        outline:
          "border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500",
        link:
          "bg-transparent text-primary-600 hover:text-primary-700 underline focus:ring-primary-500 shadow-none p-0"
      };
      return variants[this.variant] || variants.primary;
    },
    buttonStyles() {
      if (this.variant === "primary" && !this.disabled && !this.loading) {
        return "box-shadow: 0 10px 25px rgba(37, 99, 235, 0.2);";
      }
      return "";
    },
    badgeClasses() {
      return [
        "ml-2 px-2 py-0.5 rounded text-sm",
        this.variant === "primary"
          ? "bg-primary-700/50 text-white"
          : "bg-gray-200 text-gray-700"
      ].join(" ");
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("click", event);
      }
    }
  }
};
</script>
